import {TagInput, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {EnterIcon, WarningIcon} from "../../common/icons";
import StyledNumericInput from "../../common/StyledNumericInput";
import {debounce} from "lodash";
import {TVehicleConfiguration, TVehiclePieceCountValue} from "./VehicleConfiguration";
import {DefaultVehicleIcons, unknown} from "../../vehicles/VehicleIconResolver";
import {COLORS} from "../../../constants/constants";
import {PIECE_COUNT_RANGE_ERROR, gridTemplateColumns} from "./VehicleConfiguration.constants";

type VehicleConfigurationItemProps = {
  configuration: TVehicleConfiguration;
  showPieceCount: boolean;
  existingAssociatedStrings: string[];
  onUpdateConfiguration: (prefItem: TVehicleConfiguration, clearPieceCount?: boolean) => void;
  onUpdatePieceCount: (id: string, value: TVehiclePieceCountValue) => void;
};

const VehicleConfigurationItem = ({
  configuration,
  showPieceCount,
  existingAssociatedStrings,
  onUpdateConfiguration,
  onUpdatePieceCount
}: VehicleConfigurationItemProps) => {
  const [associatedStringValues, setAssociatedStringValues] = useState<string[]>([]);

  const [associatedStringsInputValue, setAssociatedStringsInputValue] = useState("");
  const [showEnterMessage, setShowEnterMessage] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [showError, setShowError] = useState(false);

  const [dwellTime, setDwellTime] = useState<number | undefined>(undefined);
  const [pieceCountMin, setPieceCountMin] = useState<number | null>(null);
  const [pieceCountMax, setPieceCountMax] = useState<number | null>(null);

  const isPieceCountRangeInvalid = pieceCountMin !== null && pieceCountMax !== null && pieceCountMin > pieceCountMax;

  useEffect(() => {
    if (configuration.value.length > 0) {
      setAssociatedStringValues(configuration.value);
    }
    setDwellTime(configuration.dwellTime);
  }, [configuration.value, configuration.dwellTime]);

  useEffect(() => {
    setPieceCountMin(configuration.pieceCount?.min ?? null);
    setPieceCountMax(configuration.pieceCount?.max ?? null);
  }, [configuration.pieceCount?.min, configuration.pieceCount?.max]);

  const onAddAssociatedString = useCallback(
    (addValues: string[]) => {
      if (addValues.length === 0) {
        setShowError(false);
        setAssociatedStringsInputValue("");
      } else {
        const newValue = addValues[0].trim();
        if (existingAssociatedStrings.includes(newValue.toLocaleLowerCase())) {
          setShowError(true);
        } else {
          const updatedValues = [...associatedStringValues, newValue];
          setAssociatedStringValues(updatedValues);
          onUpdateConfiguration({...configuration, value: updatedValues});
          setAssociatedStringsInputValue("");
          setShowError(false);
        }
      }
      setShowEnterMessage(false);
    },
    [existingAssociatedStrings, associatedStringValues, onUpdateConfiguration, configuration]
  );

  const onAssociatedStringInputChange = useCallback((e) => {
    setAssociatedStringsInputValue(e.target.value);
    setShowEnterMessage(Boolean((e.target as HTMLInputElement).value.trim()));
    setShowError(false);
  }, []);

  const onAssociatedStringInputBlur = useCallback(() => {
    setIsFocus(false);
    setShowError(false);
  }, []);

  const onAssociatedStringInputFocus = useCallback((e) => {
    setShowEnterMessage(Boolean((e.target as HTMLInputElement).value.trim()));
    setIsFocus(true);
    setShowError(false);
  }, []);

  // One debounce instance per row for the component's lifetime, both for dwell time and piece count.
  // Re-creating one whenever its callback changes identity would leave the previous instance's timer
  // armed and un-cancellable; closing over `configuration` instead would freeze it at the first
  // render, so a debounced write would restore that render's associated strings and dwell time.
  const updateConfigurationRef = useRef(onUpdateConfiguration);
  updateConfigurationRef.current = onUpdateConfiguration;
  const updatePieceCountRef = useRef(onUpdatePieceCount);
  updatePieceCountRef.current = onUpdatePieceCount;
  const configurationRef = useRef(configuration);
  configurationRef.current = configuration;

  const debouncedUpdateDwellTime = useRef(
    debounce((value: number | undefined) => {
      updateConfigurationRef.current({...configurationRef.current, dwellTime: value});
    }, 500)
  ).current;

  const onDwellTimeChange = useCallback(
    (valueAsNumber: number | undefined, valueAsString: string) => {
      const finalValue = valueAsString ? Number(valueAsNumber) : undefined;
      setDwellTime(finalValue);
      debouncedUpdateDwellTime(finalValue);
    },
    [debouncedUpdateDwellTime]
  );

  const debouncedUpdatePieceCount = useRef(
    debounce((value: TVehiclePieceCountValue) => {
      updatePieceCountRef.current(configurationRef.current.id, value);
    }, 500)
  ).current;

  // Flush rather than cancel: switching settings tabs unmounts the panel, and a value the user just
  // typed should still be saved.
  useEffect(() => () => debouncedUpdatePieceCount.flush(), [debouncedUpdatePieceCount]);

  const onRemoveAssociatedString = useCallback(
    (removeValue) => {
      const updatedAssociatedStrings = associatedStringValues.filter((value) => value !== removeValue);
      let updatedDwellTime = dwellTime;
      const isLastAssociatedStringRemoved = updatedAssociatedStrings.length === 0;
      // clear dwell time and piece count if no associated strings left
      if (isLastAssociatedStringRemoved) {
        // A value typed within the last 500ms still has a write armed. Left alone it fires after this
        // clear and re-attaches the very value the user just watched disappear: the disabled input
        // repopulates, a second mutation goes out, and re-adding a string persists it again.
        debouncedUpdatePieceCount.cancel();
        debouncedUpdateDwellTime.cancel();
        updatedDwellTime = undefined;
        setDwellTime(updatedDwellTime);
        setPieceCountMin(null);
        setPieceCountMax(null);
      }
      setAssociatedStringValues(updatedAssociatedStrings);
      onUpdateConfiguration(
        {id: configuration.id, value: updatedAssociatedStrings, dwellTime: updatedDwellTime},
        isLastAssociatedStringRemoved
      );
      setShowError(false);
    },
    [
      associatedStringValues,
      configuration.id,
      dwellTime,
      onUpdateConfiguration,
      debouncedUpdatePieceCount,
      debouncedUpdateDwellTime
    ]
  );

  const onPieceCountChange = useCallback(
    (bound: "min" | "max") => (valueAsNumber: number | undefined, valueAsString: string) => {
      // Blueprint's allowNumericCharactersOnly still permits the decimal separator, so a lone "."
      // arrives as NaN with a truthy string — truncate, and treat anything non-finite as empty.
      const parsed = Number(valueAsNumber);
      const finalValue = valueAsString && Number.isFinite(parsed) ? Math.trunc(parsed) : null;
      const updatedValue: TVehiclePieceCountValue =
        bound === "min" ? {min: finalValue, max: pieceCountMax} : {min: pieceCountMin, max: finalValue};

      setPieceCountMin(updatedValue.min);
      setPieceCountMax(updatedValue.max);

      if (updatedValue.min !== null && updatedValue.max !== null && updatedValue.min > updatedValue.max) {
        debouncedUpdatePieceCount.cancel();
        return;
      }
      debouncedUpdatePieceCount(updatedValue);
    },
    [debouncedUpdatePieceCount, pieceCountMin, pieceCountMax]
  );

  return (
    <Container data-testid="vehicle-configuration-item" isFocus={isFocus} hasPieceCount={showPieceCount}>
      <Cell data-testid="item-icon">
        <VehicleIcon
          icon={DefaultVehicleIcons.find((item) => item.id === configuration.id)?.icon || unknown}
          size="lg"
          style={{margin: "auto"}}
        />
      </Cell>
      <Cell data-testid="item-associated-strings">
        <Tooltip
          isOpen={showError}
          position="bottom-left"
          minimal
          intent="danger"
          content={<span data-testid="error-message">This custom string was already used!</span>}
        >
          <StyledTagInput
            values={associatedStringValues}
            onAdd={onAddAssociatedString}
            onRemove={onRemoveAssociatedString}
            addOnBlur={true}
            inputValue={associatedStringsInputValue}
            onInputChange={onAssociatedStringInputChange}
            inputProps={{
              onFocus: onAssociatedStringInputFocus,
              onBlur: onAssociatedStringInputBlur
            }}
            rightElement={
              showEnterMessage ? (
                <EnterMessage>
                  Press enter to add <EnterIcon />
                </EnterMessage>
              ) : undefined
            }
          />
        </Tooltip>
      </Cell>
      <Cell>
        <StyledNumericInput
          data-testid="item-dwell-time-input"
          onValueChange={onDwellTimeChange}
          value={dwellTime}
          style={{width: "60px"}}
          allowNegative={false}
          disabled={!associatedStringValues.length}
          max={60}
          min={1}
        />
      </Cell>
      {showPieceCount && (
        <PieceCountCell>
          <PieceCountInputs>
            <span>Min</span>
            <StyledNumericInput
              data-testid="item-piece-count-min-input"
              onValueChange={onPieceCountChange("min")}
              value={pieceCountMin ?? undefined}
              style={{width: "60px"}}
              allowNegative={false}
              disabled={!associatedStringValues.length}
              intent={isPieceCountRangeInvalid ? "danger" : "none"}
              minorStepSize={1}
              min={0}
            />
            <span>Max</span>
            <StyledNumericInput
              data-testid="item-piece-count-max-input"
              onValueChange={onPieceCountChange("max")}
              value={pieceCountMax ?? undefined}
              style={{width: "60px"}}
              allowNegative={false}
              disabled={!associatedStringValues.length}
              intent={isPieceCountRangeInvalid ? "danger" : "none"}
              minorStepSize={1}
              min={0}
            />
          </PieceCountInputs>
          {isPieceCountRangeInvalid && (
            <PieceCountError data-testid="item-piece-count-error">
              <WarningIcon fill={COLORS.TEXT_RED} />
              <span>{PIECE_COUNT_RANGE_ERROR}</span>
            </PieceCountError>
          )}
        </PieceCountCell>
      )}
    </Container>
  );
};

export default VehicleConfigurationItem;

const Container = styled.div<{isFocus: boolean; hasPieceCount: boolean}>`
  display: grid;
  min-height: 52px;
  height: fit-content;
  width: 100%;
  grid-template-columns: ${({hasPieceCount}) => gridTemplateColumns(hasPieceCount)};
  border-bottom: 1px solid #e5e7eb;
  background-color: ${(props) => (props.isFocus ? "#f9fafb" : "#fff")};

  .bp5-tag-input {
    background-color: ${(props) => (props.isFocus ? "#f9fafb" : "#fff")};
  }

  :hover {
    background-color: #f9fafb;
    .bp5-tag-input {
      background-color: #f9fafb;
    }
  }
`;

const Cell = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;

  .bp5-popover-target {
    width: 100%;
  }

  .bp5-numeric-input {
    margin: auto;
  }
`;

const PieceCountCell = styled(Cell)`
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;

  .bp5-numeric-input {
    margin: 0;
  }
`;

const PieceCountInputs = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const PieceCountError = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${COLORS.TEXT_RED};
  display: grid;
  grid-template-columns: 18px auto;
  align-items: start;
  gap: 5px;
`;

const VehicleIcon = styled(FontAwesomeIcon)`
  --fa-primary-color: #ffffff;
  --fa-secondary-color: #161616;
  --fa-primary-opacity: 1;
  --fa-secondary-opacity: 1;
`;

const EnterMessage = styled.div`
  margin-top: auto;
  margin-bottom: 5px;
  font-size: 12px;
  gap: 4px;
  color: #9ca3af;
  display: flex;
  align-items: center;
`;

const StyledTagInput = styled(TagInput)`
  box-shadow: none !important;
  padding: 0;

  .bp5-tag {
    height: 20px;
    border-radius: 6px;
    background-color: #dbeafe;
    color: #1e40af;
    font-size: 14px;
    padding: 4px 8px;

    .bp5-icon-small-cross {
      color: #3f61cf;
    }
  }
`;
